import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Typography,
  Button,
  Chip,
  Checkbox,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Paper,
  CircularProgress,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Pagination,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListSubheader,
  ListItemText,
  Stack,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import StyleOutlinedIcon from '@mui/icons-material/StyleOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { apiGet, apiPost, apiPut } from '../../api/client';
import { fetchSubscriberContactListsAll, type SubscriberContactListRow } from '../../api/subscribersApi';
import type { Edge, Node } from '@xyflow/react';
import type { Campaign, Scenario } from '../../domain/mailingDomain';
import {
  campaignCanLaunch,
  campaignCanActivateEvent,
  campaignChainAttachmentLocked,
  campaignChannelsFromVisualNodes,
  campaignDisplayStatus,
  campaignNamesLinkedToVisualChain,
  channelsRequiredByScenario,
  normalizeCampaignFromApi,
  resolveScenarioForCampaign,
  sortSteps,
  visualChainLockedByLaunchedCampaign,
} from '../../domain/mailingDomain';
import {
  buildAllCascadeTemplates,
  groupTemplatesByLength,
  TEMPLATE_CHANNEL_ORDER,
  type CascadeTemplateDef,
} from '../../domain/cascadeTemplates';
import {
  SMS_PAGE,
  useSmsPageDesign,
  smsPageTokens,
  smsPaperCardSx,
  smsChipBotUsernameSx,
  smsAssignScenarioButtonSx,
  smsStartBotButtonSx,
  smsTokenFieldSx,
} from '../../theme/smsPageDesign';
import {
  campaignChannelsUsed,
  normalizeStartNodeData,
  visualChainIsEventTrigger,
  type MailingCampaign,
  type MailingChannel,
} from '../mailing-constructor/mailingTypes';
import { deleteCampaign } from '../mailing-constructor/mailingStorage';
import { fetchVisualMailings, replaceVisualMailings } from '../mailing-constructor/visualMailingsApi';
import { linearizeVisualChannelSteps } from '../mailing-constructor/visualChainLinearize';
import {
  openNewVisualMailing,
  openVisualTemplateMailing,
  openAndLinkNewVisualMailingForCampaign,
} from '../mailing-constructor/visualMailingNavigate';
import ScenarioPreviewCard from './ScenarioPreviewCard';
import ScenarioPreviewDialog from './ScenarioPreviewDialog';
import CampaignSettingsDialog from './CampaignSettingsDialog';
import LinkScenarioDialog from './LinkScenarioDialog';
import i18next from '../../i18n/initI18n';
import { useTranslation } from 'react-i18next';

type MainTab = 'campaigns' | 'scenarios' | 'templates';
type ChainLinkFilter = 'all' | 'linked' | 'unlinked';
type TemplateAvailabilityFilter = 'all' | 'available' | 'development';
type TemplateLengthFilter = 'all' | 2 | 3 | 4 | 5;

type PreviewState = { kind: 'template'; def: CascadeTemplateDef } | { kind: 'local'; m: MailingCampaign };

function formatDateTime(ms: number): string {
  const locale = i18next.language?.startsWith('en') ? 'en-US' : 'ru-RU';
  return new Date(ms).toLocaleString(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function buildScenarioBadge(channels: MailingChannel[]): string {
  if (!channels.length) return i18next.t('common.emptyPath');
  const slug = channels.join('-');
  return slug.length > 40 ? `${slug.slice(0, 37)}…` : slug;
}


/** Карточек шаблонов на страницу (сетка 2×3 на md — ровные ряды). */
const TEMPLATE_CARDS_PAGE_SIZE = 24;
const CAMPAIGN_LIST_PAGE_SIZE = 5;

function parseMainTab(raw: string | null): MainTab {
  if (raw === 'scenarios' || raw === 'templates') return raw;
  return 'campaigns';
}

const mainTabIndex: Record<MainTab, number> = { campaigns: 0, scenarios: 1, templates: 2 };

function paperStack(theme: Theme, isSmsLight: boolean, extra: SxProps<Theme>): SxProps<Theme> {
  return [smsPaperCardSx(theme, isSmsLight), extra] as SxProps<Theme>;
}

const selectedSubscriberChipSx: SxProps<Theme> = {
  bgcolor: SMS_PAGE.accent,
  color: SMS_PAGE.text,
  border: `1px solid ${SMS_PAGE.accentHover}`,
  fontWeight: 700,
  '& .MuiChip-label': { px: 1.1 },
};

/** Каналы в порядке обхода графа; если линеаризация недоступна — уникальные узлы каналов. */
function mailingChannelsInFlowOrder(nodes: Node[], edges: Edge[]): MailingChannel[] {
  if (nodes.length && edges.length) {
    const ordered = linearizeVisualChannelSteps(nodes, edges).map((s) => s.channel);
    if (ordered.length) return ordered;
  }
  return campaignChannelsUsed(nodes);
}

/**
 * Чип «путь»: шаги серверного сценария; если пусто (рассинхрон/старые данные) —
 * порядок каналов с визуальной схемы.
 */
function mirroredOrVisualChainBadge(scenario: Scenario | undefined, mailing: MailingCampaign): string {
  if (scenario) {
    const fromScenario = channelsRequiredByScenario(sortSteps(scenario.steps || []));
    if (fromScenario.length) return buildScenarioBadge(fromScenario);
  }
  return buildScenarioBadge(mailingChannelsInFlowOrder(mailing.nodes, mailing.edges));
}

function formatInputDate(value: string): string {
  const [year, month, day] = value.split('-');
  if (!year || !month || !day) return value;
  return `${day}.${month}.${year}`;
}

function visualChainLaunchLine(mailing: MailingCampaign | undefined): string {
  if (!mailing) return i18next.t('campaignsPage.launchNotSet');
  const startNode = mailing.nodes.find((n) => n.type === 'mailingStart');
  const startData = startNode ? normalizeStartNodeData(startNode.data) : null;
  if (startData?.launchMode === 'event' || startData?.trigger === 'event') {
    if (!startData.scheduleTime) return i18next.t('campaignsPage.eventLaunchNotSet');
    return i18next.t('campaignsPage.eventLaunchLine', {
      type: i18next.t(`flowNodes.eventTypes.${startData.eventType ?? 'birthday'}`),
      time: startData.scheduleTime,
    });
  }
  if (!startData?.scheduleDate || !startData.scheduleTime) return i18next.t('campaignsPage.launchNotSet');
  return i18next.t('campaignsPage.launchScheduled', {
    date: formatInputDate(startData.scheduleDate),
    time: startData.scheduleTime,
  });
}

function duplicateVisualChainId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return `mc_${crypto.randomUUID()}`;
  return `mc_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 9)}`;
}

function uniqueDuplicatedChainName(baseName: string, existing: MailingCampaign[]): string {
  const base = i18next.t('campaignsPage.duplicatedChainName', { name: baseName.trim() || i18next.t('common.unnamed') });
  const existingNames = new Set(existing.map((m) => String(m.name || '').trim().toLowerCase()));
  let next = base;
  let i = 2;
  while (existingNames.has(next.trim().toLowerCase())) {
    next = `${base} ${i}`;
    i += 1;
  }
  return next;
}

function duplicateVisualChainWithoutCampaignAndTime(source: MailingCampaign, existing: MailingCampaign[]): MailingCampaign {
  const now = Date.now();
  return {
    ...source,
    id: duplicateVisualChainId(),
    name: uniqueDuplicatedChainName(source.name, existing),
    createdAt: now,
    updatedAt: now,
    nodes: source.nodes.map((node) => {
      const data = { ...((node.data ?? {}) as Record<string, unknown>) };
      if ('scheduleDate' in data) data.scheduleDate = '';
      if ('scheduleTime' in data) data.scheduleTime = '';
      return {
        ...node,
        selected: false,
        dragging: false,
        data,
        position: { ...node.position },
      };
    }),
    edges: source.edges.map((edge) => ({ ...edge, selected: false })),
  };
}

const CampaignsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const settingsFromUrl = searchParams.get('settings');
  const editLegacyParam = searchParams.get('edit');
  const mainTab: MainTab = parseMainTab(searchParams.get('tab'));
  const setMainTab = (t: MainTab) => {
    if (t === 'campaigns') {
      setSearchParams({}, { replace: true });
    } else {
      setSearchParams({ tab: t }, { replace: true });
    }
  };
  const { theme, isSmsLight } = useSmsPageDesign();
  const pageTokens = smsPageTokens(isSmsLight);
  const compactPagination = useMediaQuery(theme.breakpoints.down('sm'));
  const [rows, setRows] = useState<Campaign[]>([]);
  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creatingKey, setCreatingKey] = useState<string | null>(null);
  const [infoMsg, setInfoMsg] = useState<string | null>(null);
  const [localFlows, setLocalFlows] = useState<MailingCampaign[]>([]);
  const [preview, setPreview] = useState<PreviewState | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [linkScenarioOpen, setLinkScenarioOpen] = useState(false);
  /** Toast после смены привязанной визуальной цепочки у кампании. */
  const [chainLinkToast, setChainLinkToast] = useState<string | null>(null);
  const [flowDeleteDialog, setFlowDeleteDialog] = useState<MailingCampaign | null>(null);
  const [deletingFlow, setDeletingFlow] = useState(false);
  const [duplicatingFlowId, setDuplicatingFlowId] = useState<string | null>(null);
  const [newCampaignName, setNewCampaignName] = useState('');
  const [newCampaignSubscriberListIds, setNewCampaignSubscriberListIds] = useState<string[]>([]);
  const [newCampaignSubscriberListMenuSearch, setNewCampaignSubscriberListMenuSearch] = useState('');
  const [subscriberListsRows, setSubscriberListsRows] = useState<SubscriberContactListRow[]>([]);
  const [subscriberListsErr, setSubscriberListsErr] = useState<string | null>(null);
  const [creatingInline, setCreatingInline] = useState(false);
  const [campaignLaunchLoading, setCampaignLaunchLoading] = useState(false);
  const [providerConfirmDialog, setProviderConfirmDialog] = useState<{
    campaignId: string;
    messageId: string;
    confirmKind: 'viber' | 'sms' | 'email' | 'sms_viber';
  } | null>(null);
  const [providerConfirmLoading, setProviderConfirmLoading] = useState(false);
  /** Страница пагинации по числу каналов в шаблоне (2–5). */
  const [templatePageByLen, setTemplatePageByLen] = useState<Record<number, number>>({});
  const [templateSearch, setTemplateSearch] = useState('');
  const [templateLengthFilter, setTemplateLengthFilter] = useState<TemplateLengthFilter>('all');
  const [templateAvailabilityFilter, setTemplateAvailabilityFilter] = useState<TemplateAvailabilityFilter>('all');
  const [templateChannelFilters, setTemplateChannelFilters] = useState<MailingChannel[]>([]);
  const [campaignBucket, setCampaignBucket] = useState<'active' | 'archived'>('active');
  const [campaignSearch, setCampaignSearch] = useState('');
  const [campaignPage, setCampaignPage] = useState(1);
  const [chainSearch, setChainSearch] = useState('');
  const [chainLinkFilter, setChainLinkFilter] = useState<ChainLinkFilter>('all');
  const [archivingId, setArchivingId] = useState<string | null>(null);
  const prevMainTabRef = useRef<MainTab | null>(null);

  const loadAll = () =>
    Promise.allSettled([
      apiGet<Campaign[]>('/api/campaigns'),
      apiGet<Scenario[]>('/api/scenarios'),
      fetchVisualMailings(),
    ]).then((results) => {
      const msgs: string[] = [];
      const [rCamp, rScn, rFlows] = results;
      if (rCamp.status === 'fulfilled') {
        setRows(rCamp.value.map(normalizeCampaignFromApi));
      } else {
        const m = rCamp.reason instanceof Error ? rCamp.reason.message : String(rCamp.reason);
        msgs.push(t('campaignsPage.loadErrCampaigns', { msg: m }));
      }
      if (rScn.status === 'fulfilled') {
        setScenarios(rScn.value);
      }
      if (rFlows.status === 'fulfilled') {
        setLocalFlows(rFlows.value);
      } else {
        const m = rFlows.reason instanceof Error ? rFlows.reason.message : String(rFlows.reason);
        msgs.push(t('campaignsPage.loadErrFlows', { msg: m }));
      }
      setErr(msgs.length ? msgs.join(' ') : null);
    });

  useEffect(() => {
    loadAll();
  }, []);

  useEffect(() => {
    let cancelled = false;
    setSubscriberListsErr(null);
    void fetchSubscriberContactListsAll()
      .then((list) => {
        if (!cancelled) setSubscriberListsRows(list);
      })
      .catch((e: Error) => {
        if (!cancelled) setSubscriberListsErr(e.message);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const newCampaignFilteredSubscriberLists = useMemo(() => {
    const q = newCampaignSubscriberListMenuSearch.trim().toLowerCase();
    if (!q) return subscriberListsRows;
    return subscriberListsRows.filter((row) => {
      const name = String(row.name ?? '').toLowerCase();
      const id = String(row.contactListId);
      const cnt = row.contactsCount != null ? String(row.contactsCount) : '';
      return name.includes(q) || id.includes(q) || (cnt && cnt.includes(q));
    });
  }, [subscriberListsRows, newCampaignSubscriberListMenuSearch]);

  /** Legacy `?edit=id` → `?settings=id` (открыть настройки кампании); strip invalid `?edit=new`. */
  useEffect(() => {
    if (!editLegacyParam) return;
    if (editLegacyParam === 'new') {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('edit');
        return next;
      }, { replace: true });
      return;
    }
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('edit');
      next.set('settings', editLegacyParam);
      return next;
    }, { replace: true });
  }, [editLegacyParam, setSearchParams]);

  /** Deep link `?settings=campaignId` — выбор кампании и диалог настроек */
  useEffect(() => {
    if (!settingsFromUrl) return;
    if (rows.length === 0) return;
    const exists = rows.some((c) => c.id === settingsFromUrl);
    if (exists) {
      setSelectedId(settingsFromUrl);
      setSettingsOpen(true);
    }
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('settings');
      return next;
    }, { replace: true });
  }, [settingsFromUrl, rows, setSearchParams]);

  const createInlineCampaign = async () => {
    const n = newCampaignName.trim();
    const sids = newCampaignSubscriberListIds.filter((x) => String(x).trim());
    if (!n || !sids.length) return;
    const duplicateName = rows.some((c) => String(c.name || '').trim().toLowerCase() === n.toLowerCase());
    if (duplicateName) {
      setErr(t('campaignsPage.campaignNameDuplicate'));
      return;
    }
    setCreatingInline(true);
    setErr(null);
    try {
      const created = await apiPost<Campaign>('/api/campaigns', {
        name: n,
        scenarioId: null,
        audience: [],
        settings: {
          subscriberContactListIds: sids,
          subscriberContactListId: sids[0],
        },
      });
      setRows((prev) => {
        const rest = prev.filter((c) => c.id !== created.id);
        return [normalizeCampaignFromApi(created), ...rest];
      });
      setSelectedId(created.id);
      setNewCampaignName('');
      setNewCampaignSubscriberListIds([]);
      await loadAll();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setCreatingInline(false);
    }
  };

  useEffect(() => {
    if (mainTab !== 'scenarios') {
      prevMainTabRef.current = mainTab;
      return;
    }
    const prev = prevMainTabRef.current;
    prevMainTabRef.current = mainTab;
    /** На первом заходе с ?tab=scenarios loadAll() уже тянет цепочки; второй fetch даёт гонку и .catch([]) мог обнулить список. */
    if (prev === null) return;
    if (prev === 'scenarios') return;
    void fetchVisualMailings().then(setLocalFlows);
  }, [mainTab]);

  const sortedCampaigns = useMemo(() => [...rows].sort((a, b) => b.updatedAt - a.updatedAt), [rows]);

  const filteredCampaignsByBucket = useMemo(
    () =>
      sortedCampaigns.filter((c) => (campaignBucket === 'archived' ? Boolean(c.archived) : !c.archived)),
    [sortedCampaigns, campaignBucket],
  );

  const displayedCampaigns = useMemo(() => {
    const q = campaignSearch.trim().toLowerCase();
    if (!q) return filteredCampaignsByBucket;
    return filteredCampaignsByBucket.filter((c) => String(c.name || '').toLowerCase().includes(q));
  }, [filteredCampaignsByBucket, campaignSearch]);

  const campaignTotalPages = Math.max(1, Math.ceil(displayedCampaigns.length / CAMPAIGN_LIST_PAGE_SIZE));
  const campaignPageSafe = Math.min(Math.max(1, campaignPage), campaignTotalPages);
  const paginatedCampaigns = useMemo(() => {
    const start = (campaignPageSafe - 1) * CAMPAIGN_LIST_PAGE_SIZE;
    return displayedCampaigns.slice(start, start + CAMPAIGN_LIST_PAGE_SIZE);
  }, [displayedCampaigns, campaignPageSafe]);

  const activeCampaignCount = useMemo(
    () => sortedCampaigns.filter((c) => !c.archived).length,
    [sortedCampaigns],
  );
  const archivedCampaignCount = useMemo(
    () => sortedCampaigns.filter((c) => c.archived).length,
    [sortedCampaigns],
  );

  const archiveCampaign = async (c: Campaign, toArchived: boolean) => {
    setArchivingId(c.id);
    setErr(null);
    try {
      await apiPost<{ ok: boolean }>(`/api/campaigns/${c.id}/${toArchived ? 'archive' : 'unarchive'}`, {});
      await loadAll();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setArchivingId(null);
    }
  };

  useEffect(() => {
    if (settingsOpen) return;
    const still = selectedId && displayedCampaigns.some((c) => c.id === selectedId);
    if (!still) setSelectedId(displayedCampaigns[0]?.id ?? null);
  }, [displayedCampaigns, selectedId, settingsOpen]);

  useEffect(() => {
    setCampaignPage(1);
  }, [campaignBucket, campaignSearch]);

  useEffect(() => {
    if (campaignPage > campaignTotalPages) setCampaignPage(campaignTotalPages);
  }, [campaignPage, campaignTotalPages]);

  useEffect(() => {
    setTemplatePageByLen({});
  }, [templateAvailabilityFilter, templateChannelFilters, templateLengthFilter, templateSearch]);

  const allTemplates = useMemo(() => buildAllCascadeTemplates(), [t]);
  const filteredTemplates = useMemo(() => {
    const q = templateSearch.trim().toLowerCase();
    return allTemplates.filter((def) => {
      if (templateLengthFilter !== 'all' && def.channels.length !== templateLengthFilter) return false;
      if (templateAvailabilityFilter === 'available' && def.inDevelopment) return false;
      if (templateAvailabilityFilter === 'development' && !def.inDevelopment) return false;
      if (templateChannelFilters.length && !templateChannelFilters.every((ch) => def.channels.includes(ch))) {
        return false;
      }
      if (!q) return true;
      const haystack = [
        def.title,
        def.channels.join(' '),
        def.channels.map((ch) => t(`channels.${ch}`)).join(' '),
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [allTemplates, templateAvailabilityFilter, templateChannelFilters, templateLengthFilter, templateSearch, t]);
  const templateGroups = useMemo(() => groupTemplatesByLength(filteredTemplates), [filteredTemplates]);
  const sortedLocalFlows = useMemo(() => [...localFlows].sort((a, b) => b.updatedAt - a.updatedAt), [localFlows]);
  const linkedChainIds = useMemo(() => {
    const ids = new Set<string>();
    rows.forEach((c) => {
      const id = String(c.settings?.visualMailingId || '').trim();
      if (id) ids.add(id);
    });
    return ids;
  }, [rows]);
  const searchedLocalFlows = useMemo(() => {
    const q = chainSearch.trim().toLowerCase();
    if (!q) return sortedLocalFlows;
    return sortedLocalFlows.filter((m) => String(m.name || '').toLowerCase().includes(q));
  }, [sortedLocalFlows, chainSearch]);
  const chainFilterCounts = useMemo(() => {
    const linked = searchedLocalFlows.filter((m) => linkedChainIds.has(m.id)).length;
    return {
      all: searchedLocalFlows.length,
      linked,
      unlinked: searchedLocalFlows.length - linked,
    };
  }, [searchedLocalFlows, linkedChainIds]);
  const filteredLocalFlows = useMemo(() => {
    if (chainLinkFilter === 'all') return searchedLocalFlows;
    return searchedLocalFlows.filter((m) => {
      const isLinked = linkedChainIds.has(m.id);
      return chainLinkFilter === 'linked' ? isLinked : !isLinked;
    });
  }, [searchedLocalFlows, chainLinkFilter, linkedChainIds]);

  const newCampaignCanSubmit = useMemo(
    () =>
      Boolean(newCampaignName.trim()) &&
      newCampaignSubscriberListIds.length > 0 &&
      subscriberListsRows.length > 0 &&
      !creatingInline,
    [newCampaignName, newCampaignSubscriberListIds, subscriberListsRows.length, creatingInline]
  );

  const newCampaignCreateTooltip = useMemo(() => {
    if (creatingInline) return '';
    if (subscriberListsRows.length === 0) return t('campaignsPage.newCampaign.noSubscriberLists');
    if (!newCampaignName.trim()) return t('campaignsPage.newCampaign.needName');
    if (!newCampaignSubscriberListIds.length) return t('campaignsPage.newCampaign.needSubscriberList');
    return '';
  }, [
    creatingInline,
    newCampaignName,
    newCampaignSubscriberListIds,
    subscriberListsRows.length,
    t,
  ]);

  const campaignsLinkedToFlowDelete = useMemo(() => {
    if (!flowDeleteDialog) return [];
    return rows.filter((c) => c.settings?.visualMailingId === flowDeleteDialog.id);
  }, [flowDeleteDialog, rows]);

  const flowDeleteBlockedByCampaignState = useMemo(
    () => campaignsLinkedToFlowDelete.some((c) => campaignChainAttachmentLocked(c)),
    [campaignsLinkedToFlowDelete],
  );

  const campaignListScenarioLine = (c: Campaign) => {
    const vid = c.settings?.visualMailingId;
    if (vid) {
      const m = sortedLocalFlows.find((x) => x.id === vid);
      if (m) return m.name;
      const sc = resolveScenarioForCampaign(c, scenarios);
      if (sc) return sc.name;
    }
    return t('common.notAssigned');
  };

  const campaignListStatusLabel = (c: Campaign) => {
    if (c.status === 'active') return t('campaignsPage.status.active');
    if (c.status === 'activating') return t('campaignsPage.status.activating');
    if (c.status === 'paused') return t('campaignsPage.status.paused');
    const d = campaignDisplayStatus(c);
    switch (d) {
      case 'draft':
        return t('campaignsPage.status.draft');
      case 'queued':
        return t('campaignsPage.status.queued');
      case 'running':
        return t('campaignsPage.status.running');
      case 'delivered':
        return t('campaignsPage.status.completed');
      case 'stopped':
        return t('campaignsPage.status.stopped');
      case 'cancelled':
        return t('campaignsPage.status.aborted');
      default:
        return t('campaignsPage.status.draft');
    }
  };

  const campaignListStatusDotColor = (c: Campaign) => {
    if (c.status === 'active') return 'success.main';
    if (c.status === 'activating') return 'info.main';
    if (c.status === 'paused') return 'warning.main';
    const d = campaignDisplayStatus(c);
    switch (d) {
      case 'cancelled':
        return 'error.main';
      case 'queued':
        return 'info.main';
      case 'delivered':
      case 'running':
        return 'success.main';
      case 'stopped':
        return 'warning.main';
      default:
        return 'text.disabled';
    }
  };

  const selected = selectedId ? rows.find((c) => c.id === selectedId) : undefined;

  useEffect(() => {
    if (!selected || selected.status !== 'paused') return;
    const reason = String(selected.settings?.eventTriggerActivationError || '').trim();
    if (!reason) return;
    setErr(t('campaignsPage.eventActivationFailed', { reason }));
  }, [selected?.id, selected?.status, selected?.settings?.eventTriggerActivationError, t]);
  const selectedFrozen = Boolean(selected && (selected.locked || selected.status === 'aborted'));
  const selectedCannotModifyChainLink = campaignChainAttachmentLocked(selected);
  const selectedScenario = useMemo(
    () => (selected ? resolveScenarioForCampaign(selected, scenarios) : undefined),
    [selected, scenarios]
  );
  const selectedVisualId = selected?.settings?.visualMailingId ?? null;
  const selectedLocalMailing = selectedVisualId ? sortedLocalFlows.find((m) => m.id === selectedVisualId) : undefined;
  /** Одна привязка через «Выбрать цепочку»: на сервере и визуал, и сценарий с тем же visualMailingId — не дублируем двумя карточками */
  const mirroredVisualServerPair = Boolean(
    selectedVisualId &&
      selectedLocalMailing &&
      selectedScenario &&
      selectedScenario.visualMailingId === selectedVisualId
  );

  const selectedIsEventChain = useMemo(() => {
    const nodes = selectedLocalMailing?.nodes;
    return Boolean(nodes?.length && visualChainIsEventTrigger(nodes));
  }, [selectedLocalMailing]);

  const selectedCampaignLaunchCheck = useMemo(() => {
    if (!selected) return { ok: false as const, reason: '—' };
    const m = selectedLocalMailing;
    const visualGraphChannels = m?.nodes?.length
      ? campaignChannelsFromVisualNodes(m.nodes as Array<{ type?: string; data?: unknown }>)
      : [];
    return campaignCanLaunch(selected, null, {
      visualGraphChannels,
      visualNodes: m?.nodes as Node[] | undefined,
      visualEdges: m?.edges as Edge[] | undefined,
    });
  }, [selected, selectedLocalMailing]);

  const selectedCampaignActivateCheck = useMemo(() => {
    if (!selected) return { ok: false as const, reason: '—' };
    const m = selectedLocalMailing;
    const visualGraphChannels = m?.nodes?.length
      ? campaignChannelsFromVisualNodes(m.nodes as Array<{ type?: string; data?: unknown }>)
      : [];
    return campaignCanActivateEvent(selected, {
      visualGraphChannels,
      visualNodes: m?.nodes as Node[] | undefined,
      visualEdges: m?.edges as Edge[] | undefined,
    });
  }, [selected, selectedLocalMailing]);

  const launchSelectedCampaign = async () => {
    if (!selected || selected.status === 'running' || campaignLaunchLoading || selectedFrozen) return;
    if (!selectedCampaignLaunchCheck.ok) return;
    const campaignId = selected.id;
    setErr(null);
    setCampaignLaunchLoading(true);
    try {
      const res = await apiPost<{ ok: boolean; campaign?: Campaign }>(`/api/campaigns/${campaignId}/start`);
      await loadAll();
      setSettingsOpen(false);
      const ps = res.campaign?.providerSubmit;
      if (
        ps?.confirmPending &&
        (ps.confirmKind === 'viber' ||
          ps.confirmKind === 'sms' ||
          ps.confirmKind === 'email' ||
          ps.confirmKind === 'sms_viber')
      ) {
        setProviderConfirmDialog({
          campaignId,
          messageId: String(ps.messageId ?? ''),
          confirmKind: ps.confirmKind,
        });
      }
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setCampaignLaunchLoading(false);
    }
  };

  const activateSelectedCampaign = async () => {
    if (
      !selected ||
      selected.status === 'active' ||
      selected.status === 'activating' ||
      campaignLaunchLoading ||
      selected.status === 'aborted'
    ) {
      return;
    }
    if (!selectedCampaignActivateCheck.ok) return;
    const campaignId = selected.id;
    setErr(null);
    setCampaignLaunchLoading(true);
    try {
      await apiPost<{ ok: boolean; campaign?: Campaign }>(`/api/campaigns/${campaignId}/activate`);
      await loadAll();
      setSettingsOpen(false);
      setInfoMsg(t('campaignsPage.eventActivating'));

      const deadline = Date.now() + 180_000;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 1500));
        const list = await apiGet<Campaign[]>('/api/campaigns');
        const row = (Array.isArray(list) ? list : [])
          .map(normalizeCampaignFromApi)
          .find((c) => c.id === campaignId);
        if (!row) break;
        if (row.status === 'active') {
          setInfoMsg(t('campaignsPage.eventActivated'));
          await loadAll();
          return;
        }
        if (row.status === 'paused') {
          const reason = String(row.settings?.eventTriggerActivationError || '').trim();
          setErr(
            t('campaignsPage.eventActivationFailed', {
              reason: reason || t('api.emptyResponse'),
            })
          );
          setInfoMsg(null);
          await loadAll();
          return;
        }
        if (row.status !== 'activating') {
          await loadAll();
          return;
        }
      }
      setErr(t('campaignsPage.eventActivationFailed', { reason: t('common.wait') }));
      await loadAll();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setCampaignLaunchLoading(false);
    }
  };

  const pauseSelectedCampaign = async () => {
    if (
      !selected ||
      (selected.status !== 'active' && selected.status !== 'activating') ||
      campaignLaunchLoading
    ) {
      return;
    }
    const campaignId = selected.id;
    setErr(null);
    setCampaignLaunchLoading(true);
    try {
      await apiPost<{ ok: boolean; campaign?: Campaign }>(`/api/campaigns/${campaignId}/pause`);
      await loadAll();
      setInfoMsg(t('campaignsPage.eventPaused'));
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setCampaignLaunchLoading(false);
    }
  };

  const submitProviderConfirm = async () => {
    if (!providerConfirmDialog) return;
    setProviderConfirmLoading(true);
    setErr(null);
    try {
      await apiPost<{ ok: boolean }>(`/api/campaigns/${providerConfirmDialog.campaignId}/provider-confirm`);
      setProviderConfirmDialog(null);
      await loadAll();
      setInfoMsg(t('campaignsPage.providerConfirmSuccess'));
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setProviderConfirmLoading(false);
    }
  };

  /** Отмена до финального шага (SMS/Viber — cancel по id; режим с email — отмена уже созданных SMS/Viber). */
  const cancelProviderConfirm = async () => {
    if (!providerConfirmDialog || providerConfirmLoading) return;
    setProviderConfirmLoading(true);
    setErr(null);
    try {
      await apiPost<{ ok: boolean }>(`/api/campaigns/${providerConfirmDialog.campaignId}/provider-cancel`);
      setProviderConfirmDialog(null);
      await loadAll();
      setInfoMsg(t('campaignsPage.providerCancelSuccess'));
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setProviderConfirmLoading(false);
    }
  };

  const unlinkSelectedVisualScenario = async () => {
    if (!selected || campaignChainAttachmentLocked(selected)) return;
    setErr(null);
    try {
      await apiPut(`/api/campaigns/${selected.id}`, {
        settings: { ...(selected.settings || {}), visualMailingId: null },
      });
      loadAll();
    } catch (e) {
      setErr((e as Error).message);
    }
  };

  const unlinkMirroredVisualServerPair = async () => {
    if (!selected || campaignChainAttachmentLocked(selected)) return;
    setErr(null);
    try {
      await apiPut(`/api/campaigns/${selected.id}`, {
        scenarioId: null,
        settings: { ...(selected.settings || {}), visualMailingId: null },
      });
      loadAll();
    } catch (e) {
      setErr((e as Error).message);
    }
  };

  const performDeleteLocalFlow = async (m: MailingCampaign) => {
    const linkedBlock = rows
      .filter((c) => c.settings?.visualMailingId === m.id)
      .some((c) => campaignChainAttachmentLocked(c));
    if (linkedBlock) {
      setErr(t('campaignsPage.deleteFlowBlocked'));
      return;
    }
    setDeletingFlow(true);
    setErr(null);
    try {
      const linked = rows.filter((c) => c.settings?.visualMailingId === m.id);
      await Promise.all(
        linked.map((c) =>
          apiPut(`/api/campaigns/${c.id}`, {
            settings: { ...(c.settings || {}), visualMailingId: null },
          })
        )
      );
      const list = await fetchVisualMailings();
      await replaceVisualMailings(deleteCampaign(list, m.id));
      setLocalFlows(await fetchVisualMailings());
      setPreview((p) => (p?.kind === 'local' && p.m.id === m.id ? null : p));
      setFlowDeleteDialog(null);
      await loadAll();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setDeletingFlow(false);
    }
  };

  const duplicateLocalFlow = async (m: MailingCampaign) => {
    setDuplicatingFlowId(m.id);
    setErr(null);
    setInfoMsg(null);
    try {
      const list = await fetchVisualMailings();
      const duplicate = duplicateVisualChainWithoutCampaignAndTime(m, list);
      await replaceVisualMailings([...list, duplicate]);
      setLocalFlows(await fetchVisualMailings());
      await loadAll();
      setInfoMsg(t('campaignsPage.chainDuplicated', { name: duplicate.name }));
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setDuplicatingFlowId(null);
    }
  };

  const tabSx = {
    minHeight: 52,
    textTransform: 'none' as const,
    fontWeight: 600,
    fontSize: 14,
    letterSpacing: '0.02em',
    '&.Mui-selected': { color: 'primary.main' },
  };

  const listItemSx = (active: boolean) => ({
    display: 'flex',
    alignItems: 'flex-start',
    gap: 1,
    px: 2,
    py: 1.5,
    cursor: 'pointer',
    borderLeft: '4px solid',
    borderColor: active ? pageTokens.accent : 'transparent',
    bgcolor: active ? 'rgba(255, 179, 0, 0.14)' : 'transparent',
    '&:hover': { bgcolor: active ? 'rgba(255, 179, 0, 0.18)' : isSmsLight ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.05)' },
  });

  const applyTemplate = async (def: CascadeTemplateDef) => {
    if (def.inDevelopment) return;
    setCreatingKey(def.key);
    setErr(null);
    setInfoMsg(null);
    try {
      await openVisualTemplateMailing(navigate, `${t('common.templatePrefix')}: ${def.title}`, def.channels);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setCreatingKey(null);
    }
  };

  const handlePreviewPrimary = () => {
    if (!preview) return;
    if (preview.kind === 'template') {
      if (preview.def.inDevelopment) return;
      void applyTemplate(preview.def);
    } else {
      navigate(`/mailing/${preview.m.id}`);
    }
    setPreview(null);
  };

  const previewDialogProps = useMemo(() => {
    if (!preview) {
      return {
        title: '',
        description: '',
        channels: [] as MailingChannel[],
        primaryLabel: '',
      };
    }
    if (preview.kind === 'template') {
      return {
        title: preview.def.title,
        description: preview.def.disabledReason ?? preview.def.description,
        channels: preview.def.channels,
        primaryLabel: preview.def.inDevelopment ? t('campaignsPage.templateInDevelopment') : t('campaignsPage.primaryUse'),
      };
    }
    return {
      title: preview.m.name,
      description: '',
      channels: campaignChannelsUsed(preview.m.nodes),
      primaryLabel: t('campaignsPage.primaryOpenEditor'),
    };
  }, [preview, t]);

  const lenLabel = (n: number) => {
    if (n === 2) return t('campaignsPage.len2');
    if (n === 3) return t('campaignsPage.len3');
    if (n === 4) return t('campaignsPage.len4');
    return t('campaignsPage.len5');
  };

  return (
    <Box
      sx={{
        maxWidth: 1200,
        mx: 'auto',
        px: { xs: 1.5, sm: 2 },
        pb: 4,
        bgcolor: pageTokens.bg,
        minHeight: 'calc(100vh - 24px)',
      }}
    >
      <Paper
        elevation={0}
        sx={paperStack(theme, isSmsLight, {
          mb: 2,
          overflow: 'hidden',
          ...(isSmsLight ? { mt: { xs: 1.5, sm: 2 } } : {}),
        })}
      >
        <Tabs
          value={mainTabIndex[mainTab]}
          onChange={(_, v) => setMainTab((['campaigns', 'scenarios', 'templates'] as const)[v])}
          variant="fullWidth"
          sx={{
            width: '100%',
            minHeight: 52,
            /** Иначе в светлой теме корень Tabs тянет белый paper (#fff) и даёт полоску/контраст с карточкой #f0f0f0 */
            bgcolor: isSmsLight ? 'transparent' : undefined,
            borderBottom: 1,
            borderColor: pageTokens.divider,
            '& .MuiTabs-indicator': { height: 3, bgcolor: 'primary.main' },
            '& .MuiTabs-flexContainer': { minHeight: 52 },
          }}
        >
          <Tab icon={<CampaignOutlinedIcon sx={{ fontSize: 22 }} />} iconPosition="start" label={t('campaignsPage.tabs.campaigns')} sx={tabSx} />
          <Tab icon={<AccountTreeOutlinedIcon sx={{ fontSize: 22 }} />} iconPosition="start" label={t('campaignsPage.tabs.chains')} sx={tabSx} />
          <Tab icon={<StyleOutlinedIcon sx={{ fontSize: 22 }} />} iconPosition="start" label={t('campaignsPage.tabs.templates')} sx={tabSx} />
        </Tabs>
      </Paper>

      {infoMsg && (
        <Alert severity="success" onClose={() => setInfoMsg(null)} sx={{ mb: 2 }}>
          {infoMsg}
        </Alert>
      )}
      {err && (
        <Typography color="error" sx={{ mb: 2 }}>
          {err}
        </Typography>
      )}

      {mainTab === 'campaigns' && (
        <>
          <Paper elevation={0} sx={paperStack(theme, isSmsLight, { p: { xs: 1.5, sm: 2 }, mb: 2 })}>
            <Stack spacing={1.5}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'flex-start', sm: 'flex-start' },
                  justifyContent: 'space-between',
                  gap: 1,
                }}
              >
                <Typography variant="subtitle1" fontWeight={800} sx={{ color: pageTokens.text, lineHeight: 1.25 }}>
                  {t('campaignsPage.newCampaign.title')}
                </Typography>
              </Box>

              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1.25}
                alignItems={{ xs: 'stretch', md: 'flex-start' }}
                sx={{
                  p: { xs: 1, sm: 1.25 },
                  borderRadius: 2,
                  bgcolor: isSmsLight ? pageTokens.white : pageTokens.row,
                  border: `1px solid ${pageTokens.outlineBorder}`,
                }}
              >
                <TextField
                  size="small"
                  fullWidth
                  placeholder={t('campaignsPage.newCampaign.placeholder')}
                  value={newCampaignName}
                  onChange={(e) => setNewCampaignName(e.target.value)}
                  disabled={creatingInline}
                  sx={{ flex: { md: '1.2 1 0' }, minWidth: { xs: 0, md: 260 }, ...smsTokenFieldSx(isSmsLight) }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newCampaignCanSubmit) {
                      e.preventDefault();
                      void createInlineCampaign();
                    }
                  }}
                />
                <FormControl
                  size="small"
                  sx={{ flex: { md: '1 1 0' }, minWidth: { xs: 0, md: 280 } }}
                  disabled={creatingInline}
                >
                  <InputLabel id="new-campaign-subscriber-list-lbl" shrink>
                    {t('campaignForm.subscriberListLabel')}
                  </InputLabel>
                  <Select
                    labelId="new-campaign-subscriber-list-lbl"
                    label={t('campaignForm.subscriberListLabel')}
                    multiple
                    displayEmpty
                    value={newCampaignSubscriberListIds}
                    onChange={(e) => {
                      const v = e.target.value;
                      const next =
                        typeof v === 'string' ? (v.split(',') as string[]) : ([...(v as string[])] as string[]);
                      setNewCampaignSubscriberListIds(next.map(String));
                    }}
                    renderValue={(selected) => {
                      const arr = selected as string[];
                      if (!arr.length) {
                        return (
                          <Typography component="span" variant="body2" sx={{ color: 'text.secondary' }}>
                            {t('campaignForm.subscriberListPlaceholder')}
                          </Typography>
                        );
                      }
                      return (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {arr.map((sid) => {
                            const row = subscriberListsRows.find((r) => String(r.contactListId) === sid);
                            const label =
                              row != null
                                ? row.contactsCount != null
                                  ? `${row.name} (${row.contactsCount})`
                                  : row.name
                                : sid;
                            return <Chip key={sid} label={label} size="small" sx={selectedSubscriberChipSx} />;
                          })}
                        </Box>
                      );
                    }}
                    MenuProps={{
                      autoFocus: false,
                      PaperProps: { sx: { maxHeight: 360 } },
                    }}
                    onClose={() => {
                      setNewCampaignSubscriberListMenuSearch('');
                    }}
                  >
                    <ListSubheader
                      sx={{
                        lineHeight: 'normal',
                        px: 1,
                        py: 1,
                        bgcolor: 'background.paper',
                      }}
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      <TextField
                        size="small"
                        fullWidth
                        placeholder={t('campaignForm.subscriberListSearchPlaceholder')}
                        value={newCampaignSubscriberListMenuSearch}
                        onChange={(e) => setNewCampaignSubscriberListMenuSearch(e.target.value)}
                        onMouseDown={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                        sx={{ mt: 0.5 }}
                      />
                    </ListSubheader>
                    {newCampaignFilteredSubscriberLists.length === 0 ? (
                      <ListSubheader sx={{ color: 'text.secondary', fontStyle: 'italic', lineHeight: 1.5 }}>
                        {t('campaignForm.subscriberListSearchEmpty')}
                      </ListSubheader>
                    ) : (
                      newCampaignFilteredSubscriberLists.map((row) => {
                        const sid = String(row.contactListId);
                        const checked = newCampaignSubscriberListIds.includes(sid);
                        return (
                          <MenuItem key={row.contactListId} value={sid}>
                            <Checkbox checked={checked} size="small" sx={{ mr: 0.5, py: 0.25 }} />
                            <ListItemText
                              primary={
                                row.contactsCount != null ? `${row.name} (${row.contactsCount})` : row.name
                              }
                            />
                          </MenuItem>
                        );
                      })
                    )}
                  </Select>
                </FormControl>

                <Box sx={{ display: 'flex', alignItems: { xs: 'stretch', md: 'flex-start' } }}>
                  <Tooltip title={newCampaignCreateTooltip} placement="top" arrow disableHoverListener={!newCampaignCreateTooltip}>
                    <span style={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        variant="contained"
                        color="primary"
                        disabled={!newCampaignCanSubmit}
                        onClick={() => void createInlineCampaign()}
                        sx={{
                          minWidth: { xs: '100%', sm: 148 },
                          height: 40,
                          borderRadius: '999px',
                          textTransform: 'none',
                          fontWeight: 700,
                          px: 2,
                          boxShadow: 'none',
                          '&:not(:disabled)': { boxShadow: isSmsLight ? '0 1px 4px rgba(15,118,110,0.18)' : undefined },
                        }}
                      >
                        {creatingInline ? (
                          <CircularProgress size={22} color="inherit" />
                        ) : (
                          t('campaignsPage.newCampaign.create')
                        )}
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
              </Stack>

              {subscriberListsErr ? (
                <Alert severity="warning" sx={{ py: 0.5 }}>
                  {t('campaignForm.subscriberListLoadError')}: {subscriberListsErr}
                </Alert>
              ) : null}
            </Stack>
          </Paper>

          <Paper
            elevation={0}
            sx={paperStack(theme, isSmsLight, {
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'minmax(240px, 300px) 1fr' },
              minHeight: 420,
              overflow: 'hidden',
            })}
          >
            <Box sx={{ borderRight: `1px solid ${pageTokens.divider}`, bgcolor: pageTokens.card }}>
              <Box sx={{ borderBottom: `1px solid ${pageTokens.divider}`, px: 1, pt: 1, pb: 1 }}>
                <ToggleButtonGroup
                  exclusive
                  fullWidth
                  size="small"
                  value={campaignBucket}
                  onChange={(_, v) => {
                    if (v !== null) setCampaignBucket(v);
                  }}
                  aria-label={t('campaignsPage.listTitle')}
                  sx={{
                    mb: 1,
                    '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 600, py: 0.75 },
                  }}
                >
                  <ToggleButton value="active">
                    {t('campaignsPage.buckets.active')} ({activeCampaignCount})
                  </ToggleButton>
                  <ToggleButton value="archived">
                    {t('campaignsPage.buckets.archived')} ({archivedCampaignCount})
                  </ToggleButton>
                </ToggleButtonGroup>
                <TextField
                  size="small"
                  fullWidth
                  placeholder={t('campaignsPage.searchPlaceholder')}
                  value={campaignSearch}
                  onChange={(e) => setCampaignSearch(e.target.value)}
                  sx={{ ...smsTokenFieldSx(isSmsLight) }}
                />
              </Box>
              <Box sx={{ maxHeight: 360, overflow: 'auto' }}>
                {rows.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                    {t('campaignsPage.noCampaigns')}
                  </Typography>
                ) : displayedCampaigns.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2, lineHeight: 1.55 }}>
                    {campaignSearch.trim()
                      ? t('campaignsPage.noSearchResults')
                      : campaignBucket === 'archived'
                        ? t('campaignsPage.emptyArchive')
                        : t('campaignsPage.allInArchive')}
                  </Typography>
                ) : (
                  paginatedCampaigns.map((c) => {
                    const toArchive = !c.archived;
                    const busy = archivingId === c.id;
                    const archiveDisabled = busy;
                    let archiveHint = '';
                    if (busy) archiveHint = t('common.wait');
                    else archiveHint = toArchive ? t('campaignsPage.archive') : t('campaignsPage.unarchive');
                    return (
                      <Box key={c.id} onClick={() => setSelectedId(c.id)} sx={listItemSx(c.id === selectedId)}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="subtitle2" fontWeight={600} noWrap>
                            {c.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block" noWrap>
                            {t('campaignsPage.chainLabel')}: {campaignListScenarioLine(c)}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5 }}>
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: campaignListStatusDotColor(c),
                              }}
                            />
                            <Typography variant="caption" color="text.secondary">
                              {campaignListStatusLabel(c)}
                            </Typography>
                          </Box>
                        </Box>
                        <Tooltip title={archiveHint}>
                          <span>
                            <IconButton
                              size="small"
                              disabled={archiveDisabled}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!archiveDisabled) void archiveCampaign(c, toArchive);
                              }}
                              aria-label={archiveHint}
                              sx={{ mt: -0.25 }}
                            >
                              {busy ? (
                                <CircularProgress size={18} color="inherit" />
                              ) : toArchive ? (
                                <ArchiveOutlinedIcon fontSize="small" />
                              ) : (
                                <UnarchiveOutlinedIcon fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    );
                  })
                )}
              </Box>
              {displayedCampaigns.length > CAMPAIGN_LIST_PAGE_SIZE ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 1, borderTop: `1px solid ${pageTokens.divider}` }}>
                  <Pagination
                    count={campaignTotalPages}
                    page={campaignPageSafe}
                    onChange={(_, p) => setCampaignPage(p)}
                    size="small"
                    color="primary"
                  />
                </Box>
              ) : null}
            </Box>

            <Box sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: pageTokens.white }}>
              {!selected ? (
                <Typography color="text.secondary">{t('campaignsPage.selectCampaign')}</Typography>
              ) : (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', minWidth: 0 }}>
                      <Tooltip
                        title={
                          selected.status === 'aborted'
                            ? t('campaignsPage.tooltipCabinetAborted')
                            : selectedFrozen
                              ? t('campaignsPage.tooltipLocked')
                              : t('campaignsPage.tooltipEditCampaign')
                        }
                      >
                        <Chip
                          label={selected.name}
                          onClick={() => !selectedFrozen && setSettingsOpen(true)}
                          sx={
                            [
                              smsChipBotUsernameSx(),
                              {
                                cursor: selectedFrozen ? 'default' : 'pointer',
                                maxWidth: '100%',
                                '&:hover': selectedFrozen ? undefined : { filter: 'brightness(1.06)' },
                              },
                            ] as SxProps<Theme>
                          }
                        />
                      </Tooltip>
                      {selected.archived ? (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={t('campaignsPage.badgeArchived')}
                          sx={{ fontWeight: 600 }}
                        />
                      ) : null}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
                      {selectedIsEventChain ? (
                        selected.status === 'active' || selected.status === 'activating' ? (
                          <Tooltip placement="top" arrow title={campaignLaunchLoading ? t('common.wait') : ''}>
                            <span>
                              <Button
                                variant="outlined"
                                color="warning"
                                size="small"
                                disabled={campaignLaunchLoading}
                                startIcon={
                                  campaignLaunchLoading ? (
                                    <CircularProgress size={16} color="inherit" />
                                  ) : selected.status === 'activating' ? (
                                    <CircularProgress size={16} color="inherit" />
                                  ) : (
                                    <PauseIcon />
                                  )
                                }
                                onClick={() => void pauseSelectedCampaign()}
                                sx={{ textTransform: 'none', borderRadius: '20px', fontWeight: 700 }}
                              >
                                {selected.status === 'activating'
                                  ? campaignLaunchLoading
                                    ? t('common.wait')
                                    : t('campaignForm.cancelPreparing')
                                  : campaignLaunchLoading
                                    ? t('common.wait')
                                    : t('campaignForm.pause')}
                              </Button>
                            </span>
                          </Tooltip>
                        ) : selected.status !== 'aborted' ? (
                          <Tooltip
                            placement="top"
                            arrow
                            disableHoverListener={
                              campaignLaunchLoading || selectedCampaignActivateCheck.ok
                            }
                            title={
                              campaignLaunchLoading
                                ? t('common.wait')
                                : !selectedCampaignActivateCheck.ok
                                  ? `${t('campaignForm.launchBlocked', {
                                      reason: selectedCampaignActivateCheck.reason,
                                    })}`
                                  : ''
                            }
                          >
                            <span>
                              <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                disabled={!selectedCampaignActivateCheck.ok || campaignLaunchLoading}
                                startIcon={
                                  campaignLaunchLoading ? (
                                    <CircularProgress size={16} color="inherit" />
                                  ) : (
                                    <PlayArrowIcon />
                                  )
                                }
                                onClick={() => void activateSelectedCampaign()}
                                sx={{ textTransform: 'none', borderRadius: '20px', fontWeight: 700 }}
                              >
                                {campaignLaunchLoading ? t('common.wait') : t('campaignForm.activate')}
                              </Button>
                            </span>
                          </Tooltip>
                        ) : null
                      ) : selected.status !== 'running' ? (
                        <Tooltip
                          placement="top"
                          arrow
                          disableHoverListener={
                            campaignLaunchLoading || (!selectedFrozen && selectedCampaignLaunchCheck.ok)
                          }
                          title={
                            campaignLaunchLoading
                              ? t('common.wait')
                              : selected.status === 'aborted'
                                ? t('domain.campaignAbortedCabinet')
                                : selectedFrozen
                                  ? t('campaignsPage.tooltipLocked')
                                  : !selectedCampaignLaunchCheck.ok
                                    ? `${t('campaignForm.launchBlocked', {
                                        reason: selectedCampaignLaunchCheck.reason,
                                      })}`
                                    : ''
                          }
                        >
                          <span>
                            <Button
                              variant="contained"
                              color="primary"
                              size="small"
                              disabled={
                                Boolean(selectedFrozen) ||
                                !selectedCampaignLaunchCheck.ok ||
                                campaignLaunchLoading
                              }
                              startIcon={
                                campaignLaunchLoading ? (
                                  <CircularProgress size={16} color="inherit" />
                                ) : (
                                  <PlayArrowIcon />
                                )
                              }
                              onClick={() => void launchSelectedCampaign()}
                              sx={{
                                textTransform: 'none',
                                borderRadius: '20px',
                                fontWeight: 700,
                              }}
                            >
                              {campaignLaunchLoading ? t('common.wait') : t('campaignForm.launch')}
                            </Button>
                          </span>
                        </Tooltip>
                      ) : null}
                      <Tooltip
                        title={
                          selectedFrozen
                            ? selected.status === 'aborted'
                              ? t('campaignsPage.tooltipCabinetAborted')
                              : t('campaignsPage.tooltipLocked')
                            : t('campaignsPage.tooltipSettings')
                        }
                      >
                        <span>
                          <IconButton disabled={selectedFrozen} onClick={() => !selectedFrozen && setSettingsOpen(true)}>
                            <SettingsOutlinedIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </Box>

                  {selected && (
                    <Typography
                      variant="body2"
                      sx={{ display: 'block', mb: 2, fontWeight: 600, color: selectedLocalMailing ? pageTokens.text : 'text.secondary' }}
                    >
                      {visualChainLaunchLine(selectedLocalMailing)}
                    </Typography>
                  )}

                  {selectedVisualId && !selectedLocalMailing && (
                    <Typography variant="body2" color="warning.main" sx={{ mb: 1 }}>
                      {t('campaignsPage.visualNotFound')}
                    </Typography>
                  )}

                  {mirroredVisualServerPair && selectedLocalMailing && selectedScenario && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        flexWrap: 'wrap',
                        p: 1.5,
                        mb: 1.5,
                        borderRadius: 1,
                        bgcolor: pageTokens.row,
                        border: 1,
                        borderColor: pageTokens.rowBorder,
                      }}
                    >
                      <Chip
                        size="small"
                        label={mirroredOrVisualChainBadge(selectedScenario, selectedLocalMailing)}
                        sx={{ fontWeight: 600, bgcolor: pageTokens.triggerChipBg, color: pageTokens.triggerChipText }}
                      />
                      <Box sx={{ flex: 1, minWidth: 140 }}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {t('campaignsPage.mirroredCaption')}
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {selectedLocalMailing.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, lineHeight: 1.35 }}>
                          {visualChainLaunchLine(selectedLocalMailing)}
                        </Typography>
                      </Box>
                      <Tooltip title={t('campaignsPage.tooltipOpenFlowEditor')}>
                        <IconButton size="small" onClick={() => navigate(`/mailing/${selectedLocalMailing.id}`)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={selectedCannotModifyChainLink ? t('campaignsPage.unlinkChainBlocked') : t('campaignsPage.tooltipUnlink')}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => unlinkMirroredVisualServerPair()}
                            disabled={selectedCannotModifyChainLink}
                          >
                            <LinkOffIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  )}

                  {selectedVisualId && selectedLocalMailing && !mirroredVisualServerPair && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        flexWrap: 'wrap',
                        p: 1.5,
                        mb: 1.5,
                        borderRadius: 1,
                        bgcolor: pageTokens.row,
                        border: 1,
                        borderColor: pageTokens.rowBorder,
                      }}
                    >
                      <Chip
                        size="small"
                        label={buildScenarioBadge(
                          mailingChannelsInFlowOrder(selectedLocalMailing.nodes, selectedLocalMailing.edges),
                        )}
                        sx={{ fontWeight: 600, bgcolor: pageTokens.triggerChipBg, color: pageTokens.triggerChipText }}
                      />
                      <Box sx={{ flex: 1, minWidth: 140 }}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {t('campaignsPage.visualLabel')}
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {selectedLocalMailing.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, lineHeight: 1.35 }}>
                          {visualChainLaunchLine(selectedLocalMailing)}
                        </Typography>
                      </Box>
                      <Tooltip title={t('campaignsPage.tooltipOpenEditor')}>
                        <IconButton size="small" onClick={() => navigate(`/mailing/${selectedLocalMailing.id}`)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={selectedCannotModifyChainLink ? t('campaignsPage.unlinkChainBlocked') : t('campaignsPage.tooltipUnlinkVisual')}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => unlinkSelectedVisualScenario()}
                            disabled={selectedCannotModifyChainLink}
                          >
                            <LinkOffIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  )}

                  {!selectedVisualId && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {t('campaignsPage.chainNotSelected')}
                      </Typography>
                    )}

                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 1,
                      mt: 2,
                      alignItems: 'center',
                    }}
                  >
                    <Button
                      variant="outlined"
                      size="small"
                      color="inherit"
                      startIcon={<StyleOutlinedIcon />}
                      onClick={() => setMainTab('templates')}
                      sx={{ textTransform: 'none', borderRadius: '12px', fontWeight: 600 }}
                    >
                      {t('campaignsPage.btnTemplates')}
                    </Button>
                    <Tooltip
                      title={selectedCannotModifyChainLink ? t('campaignsPage.selectChainBlocked') : ''}
                      disableHoverListener={!selectedCannotModifyChainLink}
                    >
                      <span>
                        <Button
                          variant="outlined"
                          startIcon={<LinkOutlinedIcon />}
                          onClick={() => {
                            if (!selectedCannotModifyChainLink) setLinkScenarioOpen(true);
                          }}
                          disabled={selectedCannotModifyChainLink}
                          sx={{
                            textTransform: 'none',
                            borderRadius: '20px',
                            fontWeight: 600,
                            ...smsAssignScenarioButtonSx(),
                          }}
                        >
                          {sortedLocalFlows.length === 0
                            ? t('campaignsPage.btnCreateChain')
                            : t('campaignsPage.btnSelectChain')}
                        </Button>
                      </span>
                    </Tooltip>
                  </Box>

                  <LinkScenarioDialog
                    open={linkScenarioOpen}
                    onClose={() => setLinkScenarioOpen(false)}
                    campaign={selected}
                    allCampaigns={rows}
                    localFlows={sortedLocalFlows}
                    onCreateNewLinkedChain={async () => {
                      const c = await openAndLinkNewVisualMailingForCampaign(
                        navigate,
                        selected.id,
                        t('common.newChain'),
                      );
                      setChainLinkToast(
                        t('linkScenario.linkedSuccessDetail', {
                          campaign: selected.name,
                          flow: c.name,
                        }),
                      );
                      void loadAll();
                    }}
                    onLinked={(detail) => {
                      setChainLinkToast(
                        t('linkScenario.linkedSuccessDetail', {
                          campaign: detail.campaignName,
                          flow: detail.flowName,
                        })
                      );
                      void loadAll();
                    }}
                  />
                </>
              )}
            </Box>
          </Paper>
        </>
      )}

      {mainTab === 'scenarios' && (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 2, flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder={t('campaignsPage.chainSearchPlaceholder')}
              value={chainSearch}
              onChange={(e) => setChainSearch(e.target.value)}
              sx={{ flex: '1 1 260px', maxWidth: 420, ...smsTokenFieldSx(isSmsLight) }}
            />
            <ToggleButtonGroup
              exclusive
              size="small"
              value={chainLinkFilter}
              onChange={(_, v: ChainLinkFilter | null) => {
                if (v) setChainLinkFilter(v);
              }}
              aria-label={t('campaignsPage.chainFilters.label')}
              sx={{
                '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 600, py: 0.75 },
              }}
            >
              <ToggleButton value="all">
                {t('campaignsPage.chainFilters.all')} ({chainFilterCounts.all})
              </ToggleButton>
              <ToggleButton value="linked">
                {t('campaignsPage.chainFilters.linked')} ({chainFilterCounts.linked})
              </ToggleButton>
              <ToggleButton value="unlinked">
                {t('campaignsPage.chainFilters.unlinked')} ({chainFilterCounts.unlinked})
              </ToggleButton>
            </ToggleButtonGroup>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() =>
                void openNewVisualMailing(navigate, t('common.newChain')).catch((e) =>
                  setErr(e instanceof Error ? e.message : String(e))
                )
              }
            >
              {t('campaignsPage.newChain')}
            </Button>
          </Box>
          {sortedLocalFlows.length === 0 ? (
            <Paper elevation={0} sx={paperStack(theme, isSmsLight, { p: 3 })}>
              <Typography color="text.secondary">{t('campaignsPage.noChains')}</Typography>
            </Paper>
          ) : filteredLocalFlows.length === 0 ? (
            <Paper elevation={0} sx={paperStack(theme, isSmsLight, { p: 3 })}>
              <Typography color="text.secondary">
                {chainSearch.trim()
                  ? t('campaignsPage.noChainSearchResults')
                  : t('campaignsPage.noChainFilterResults')}
              </Typography>
            </Paper>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                gap: 2,
              }}
            >
              {filteredLocalFlows.map((m) => {
                const chs = mailingChannelsInFlowOrder(m.nodes, m.edges);
                const linkedNames = campaignNamesLinkedToVisualChain(m.id, rows, scenarios);
                const chainDeleteLocked = visualChainLockedByLaunchedCampaign(m.id, rows);
                const linkedCampaignLine =
                  linkedNames.length === 0
                    ? t('scenarioPreview.chainNotLinked')
                    : linkedNames.length === 1
                      ? t('scenarioPreview.chainLinkedSingle', { name: linkedNames[0] })
                      : t('scenarioPreview.chainLinkedMultiple', { names: linkedNames.join(', ') });
                return (
                  <ScenarioPreviewCard
                    key={m.id}
                    badge={buildScenarioBadge(chs)}
                    title={m.name}
                    description=""
                    linkedCampaignLine={linkedCampaignLine}
                    onOpenDetails={() => setPreview({ kind: 'local', m })}
                    onUse={() => navigate(`/mailing/${m.id}`)}
                    useLabel={t('scenarioPreview.use')}
                    isSmsLight={isSmsLight}
                    onRequestDuplicate={() => void duplicateLocalFlow(m)}
                    duplicateDisabled={duplicatingFlowId === m.id}
                    onRequestDelete={() => setFlowDeleteDialog(m)}
                    deleteDisabled={deletingFlow || chainDeleteLocked}
                    deleteTooltip={
                      chainDeleteLocked ? t('mailingConstructor.chainDeleteLockedTooltip') : undefined
                    }
                  />
                );
              })}
            </Box>
          )}
        </Box>
      )}

      {mainTab === 'templates' && (
        <Box>
          <Paper elevation={0} sx={paperStack(theme, isSmsLight, { p: { xs: 1.25, sm: 1.5 }, mb: 2 })}>
            <Stack spacing={1.1}>
              <TextField
                size="small"
                fullWidth
                placeholder={t('campaignsPage.templateSearchPlaceholder')}
                value={templateSearch}
                onChange={(e) => setTemplateSearch(e.target.value)}
                sx={smsTokenFieldSx(isSmsLight)}
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
                <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 160 } }}>
                  <InputLabel id="template-length-filter-lbl">{t('campaignsPage.templateFilters.lengthLabel')}</InputLabel>
                  <Select
                    labelId="template-length-filter-lbl"
                    label={t('campaignsPage.templateFilters.lengthLabel')}
                    value={templateLengthFilter}
                    onChange={(e) => setTemplateLengthFilter(e.target.value as TemplateLengthFilter)}
                  >
                    <MenuItem value="all">{t('campaignsPage.templateFilters.allLengths')}</MenuItem>
                    {[2, 3, 4, 5].map((len) => (
                      <MenuItem key={len} value={len}>
                        {lenLabel(len)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 170 } }}>
                  <InputLabel id="template-availability-filter-lbl">{t('campaignsPage.templateFilters.availabilityLabel')}</InputLabel>
                  <Select
                    labelId="template-availability-filter-lbl"
                    label={t('campaignsPage.templateFilters.availabilityLabel')}
                    value={templateAvailabilityFilter}
                    onChange={(e) => setTemplateAvailabilityFilter(e.target.value as TemplateAvailabilityFilter)}
                  >
                    <MenuItem value="all">{t('campaignsPage.templateFilters.allAvailability')}</MenuItem>
                    <MenuItem value="available">{t('campaignsPage.templateFilters.available')}</MenuItem>
                    <MenuItem value="development">{t('campaignsPage.templateFilters.development')}</MenuItem>
                  </Select>
                </FormControl>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ ml: { sm: 'auto' }, fontWeight: 500, whiteSpace: 'nowrap' }}
                >
                  {t('campaignsPage.templateFilters.resultCount', { count: filteredTemplates.length })}
                </Typography>
              </Stack>
              <ToggleButtonGroup
                value={templateChannelFilters}
                onChange={(_, value: MailingChannel[]) => setTemplateChannelFilters(value)}
                size="small"
                aria-label={t('campaignsPage.templateFilters.channelsLabel')}
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 0.5,
                  '& .MuiToggleButtonGroup-grouped': {
                    borderRadius: '999px !important',
                    border: `1px solid ${pageTokens.outlineBorder}`,
                    px: 1.1,
                    py: 0.35,
                    fontSize: 12,
                    lineHeight: 1.3,
                  },
                }}
              >
                {TEMPLATE_CHANNEL_ORDER.map((ch) => (
                  <ToggleButton key={ch} value={ch}>
                    {t(`channels.${ch}`)}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Stack>
          </Paper>
          {templateGroups.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
              {t('campaignsPage.noTemplateResults')}
            </Typography>
          ) : (
            templateGroups.map(({ len, items }) => {
              const totalPages = Math.max(1, Math.ceil(items.length / TEMPLATE_CARDS_PAGE_SIZE));
              const rawPage = templatePageByLen[len] ?? 1;
              const page = Math.min(Math.max(1, rawPage), totalPages);
              const sliceStart = (page - 1) * TEMPLATE_CARDS_PAGE_SIZE;
              const sliceEnd = Math.min(sliceStart + TEMPLATE_CARDS_PAGE_SIZE, items.length);
              const visibleItems = items.slice(sliceStart, sliceEnd);
              return (
                <Box key={len} sx={{ mb: 3 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'baseline',
                      justifyContent: 'space-between',
                      gap: 1,
                      mb: 1.5,
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight={700}>
                      {lenLabel(len)} ({items.length})
                    </Typography>
                    {totalPages > 1 ? (
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                        {t('campaignsPage.shownRange', { from: sliceStart + 1, to: sliceEnd, total: items.length })}
                      </Typography>
                    ) : null}
                  </Box>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                      gap: 2,
                    }}
                  >
                    {visibleItems.map((def) => (
                      <ScenarioPreviewCard
                        key={def.key}
                        badge={buildScenarioBadge(def.channels)}
                        title={def.title}
                        description={def.disabledReason ?? ''}
                        onOpenDetails={() => setPreview({ kind: 'template', def })}
                        onUse={() => applyTemplate(def)}
                        useLabel={def.inDevelopment ? t('campaignsPage.templateInDevelopment') : t('scenarioPreview.use')}
                        useLoading={creatingKey === def.key}
                        useDisabled={def.inDevelopment || (creatingKey !== null && creatingKey !== def.key)}
                        statusLabel={def.inDevelopment ? t('campaignsPage.templateInDevelopment') : undefined}
                        disabledTooltip={def.disabledReason}
                        isSmsLight={isSmsLight}
                      />
                    ))}
                  </Box>
                  {totalPages > 1 ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                      <Pagination
                        count={totalPages}
                        page={page}
                        onChange={(_, p) => setTemplatePageByLen((prev) => ({ ...prev, [len]: p }))}
                        color="primary"
                        showFirstButton
                        showLastButton
                        size={compactPagination ? 'small' : 'medium'}
                      />
                    </Box>
                  ) : null}
                </Box>
              );
            })
          )}
        </Box>
      )}

      <ScenarioPreviewDialog
        open={preview !== null}
        onClose={() => setPreview(null)}
        title={previewDialogProps.title}
        description={previewDialogProps.description}
        channels={previewDialogProps.channels}
        primaryLabel={previewDialogProps.primaryLabel}
        onPrimary={handlePreviewPrimary}
        primaryLoading={preview?.kind === 'template' && preview.def.key === creatingKey}
        primaryDisabled={
          preview?.kind === 'template' &&
          (preview.def.inDevelopment || (creatingKey !== null && preview.def.key !== creatingKey))
        }
      />

      <Dialog
        open={flowDeleteDialog !== null}
        onClose={(_, reason) => {
          if (deletingFlow) return;
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') setFlowDeleteDialog(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('campaignsPage.deleteFlowTitle')}</DialogTitle>
        <DialogContent>
          {flowDeleteDialog ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                {t('campaignsPage.deleteFlowBody', { name: flowDeleteDialog.name })}
              </Typography>
              {flowDeleteBlockedByCampaignState ? (
                <Alert severity="warning" sx={{ mt: 1.5 }}>
                  {t('campaignsPage.deleteFlowBlockedDetail')}
                </Alert>
              ) : campaignsLinkedToFlowDelete.length > 0 ? (
                <Typography variant="body2" sx={{ mt: 1.5, lineHeight: 1.6 }}>
                  {t('campaignsPage.deleteFlowUnlink', { count: campaignsLinkedToFlowDelete.length })}
                </Typography>
              ) : null}
            </>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setFlowDeleteDialog(null)} disabled={deletingFlow} color="inherit">
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={deletingFlow || !flowDeleteDialog || flowDeleteBlockedByCampaignState}
            onClick={() => flowDeleteDialog && performDeleteLocalFlow(flowDeleteDialog)}
          >
            {deletingFlow ? t('common.deleting') : t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={providerConfirmDialog !== null}
        onClose={(_, reason) => {
          if (providerConfirmLoading) return;
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') setProviderConfirmDialog(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('campaignsPage.providerConfirmTitle')}</DialogTitle>
        <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Button
            onClick={() => void cancelProviderConfirm()}
            color="inherit"
            disabled={providerConfirmLoading}
          >
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={() => void submitProviderConfirm()}
            disabled={providerConfirmLoading}
            startIcon={providerConfirmLoading ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={{ textTransform: 'none' }}
          >
            {providerConfirmLoading ? t('common.wait') : t('campaignsPage.providerConfirmSubmit')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(chainLinkToast)}
        autoHideDuration={8000}
        onClose={() => setChainLinkToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setChainLinkToast(null)}
          severity="success"
          variant="filled"
          elevation={6}
          sx={{ alignItems: 'center' }}
        >
          {chainLinkToast}
        </Alert>
      </Snackbar>

      {selected && (
        <CampaignSettingsDialog
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          campaign={selected}
          scenarios={scenarios}
          onSaved={loadAll}
          onDeleted={() => {
            loadAll();
            setSelectedId(null);
            setSettingsOpen(false);
          }}
        />
      )}
    </Box>
  );
};

export default CampaignsPage;
